import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, homeForRole } from "../auth/AuthContext";
import { Logo } from "../components/Navbar";
import api, { fmtErr } from "../lib/api";
import { Field, inputCls } from "../portal/ui";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(homeForRole(user.role));
    } catch (err) {
      toast.error("Sign in failed", { description: fmtErr(err) });
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email });
      setForgotSent(true);
    } catch (err) {
      toast.error("Request failed", { description: fmtErr(err) });
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col items-center justify-center px-6" data-testid="login-page">
      <Link to="/" className="mb-10">
        <Logo />
      </Link>
      <div className="glass-panel rounded-2xl p-8 md:p-10 w-full max-w-md">
        <h1 className="font-outfit text-3xl font-extralight tracking-tight">Portal <span className="text-gold">Sign In</span></h1>
        <p className="text-sm text-white/50 mt-2">Customer, supplier and Ajemi team access.</p>

        {forgot ? (
          <form onSubmit={submitForgot} className="mt-8 space-y-5" data-testid="forgot-form">
            {forgotSent ? (
              <p className="text-sm text-white/60 leading-relaxed" data-testid="forgot-sent">
                If an account exists for this email, a password reset has been initiated. Contact
                the Ajemi team to complete the reset.
              </p>
            ) : (
              <>
                <Field label="Email" required>
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="name@company.com" data-testid="forgot-email-input" />
                </Field>
                <button type="submit" className="w-full bg-gold text-ink font-outfit font-medium rounded-full py-3.5 hover:bg-goldbright transition-colors" data-testid="forgot-submit-btn">
                  Reset Password
                </button>
              </>
            )}
            <button type="button" onClick={() => setForgot(false)} className="text-sm text-white/50 hover:text-gold transition-colors" data-testid="forgot-back-btn">
              ← Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <Field label="Email" required>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="name@company.com" data-testid="login-email-input" />
            </Field>
            <Field label="Password" required>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="login-password-input" />
            </Field>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-ink font-outfit font-medium rounded-full py-3.5 hover:bg-goldbright hover:shadow-[0_0_32px_rgba(217,164,65,0.4)] transition-all disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => setForgot(true)} className="text-white/45 hover:text-gold transition-colors" data-testid="forgot-link">
                Forgot password?
              </button>
              <Link to="/signup" className="text-white/45 hover:text-gold transition-colors" data-testid="goto-signup-link">
                Create account
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
